import { ContentMatch, Fragment, Node, Schema, Slice, ResolvedPos, NodeType } from 'prosemirror-model'
import { EditorState, Selection } from 'prosemirror-state'
import { canJoin, liftTarget, ReplaceAroundStep } from 'prosemirror-transform'
import { EditorView } from 'prosemirror-view'
import { setTrackedChangeCommand } from '../Plugins/TrackChanges/commands'
import { CommandName } from '../Plugins/TrackChanges/commands/types'

// Never exported from 'prosemirror-commands' but needed for the plucked out commands
export function defaultBlockAt<S extends Schema = any>(match: ContentMatch<S>) {
  for (let i = 0; i < match.edgeCount; i++) {
    const { type } = match.edge(i)
    if (type.isTextblock && !type.hasRequiredAttrs()) return type
  }
  return null
}

// Never exported from 'prosemirror-commands' but needed for the plucked out commands
export function textblockAt<S extends Schema = any>(
  node: Node<S> | null | undefined,
  side: 'start' | 'end',
  only?: boolean,
) {
  for (; node; node = side === 'start' ? node.firstChild : node.lastChild) {
    if (node.isTextblock) return true
    if (only && node.childCount !== 1) return false
  }
  return false
}

// Never exported from 'prosemirror-commands' but needed for the plucked out commands
export function findCutBefore<S extends Schema = any>($pos: ResolvedPos<S>) {
  if (!$pos.parent.type.spec.isolating)
    for (let i = $pos.depth - 1; i >= 0; i--) {
      if ($pos.index(i) > 0) return $pos.doc.resolve($pos.before(i + 1))
      if ($pos.node(i).type.spec.isolating) break
    }
  return null
}

// Never exported from 'prosemirror-commands' but needed for the plucked out commands
function joinMaybeClear<S extends Schema = any>(
  state: EditorState<S>,
  $pos: ResolvedPos<S>,
  dispatch?: EditorView<S>['dispatch'],
) {
  let before = $pos.nodeBefore,
    after = $pos.nodeAfter,
    index = $pos.index()
  if (!before || !after || !before.type.compatibleContent(after.type)) {
    return false
  }
  if (!before.content.size && $pos.parent.canReplace(index - 1, index)) {
    if (dispatch) {
      const tr = state.tr.delete($pos.pos - before.nodeSize, $pos.pos).scrollIntoView()
      // TODO: Consider rejoining this whole branch into `joinBackward` to keep this decoration from interfering with something else
      setTrackedChangeCommand(tr, { name: CommandName.JOIN_BACKWARD })
      dispatch(tr)
    }
    return true
  }
  if (!$pos.parent.canReplace(index, index + 1) || !(after.isTextblock || canJoin(state.doc, $pos.pos))) {
    return false
  }
  if (dispatch) {
    dispatch(
      state.tr
        .clearIncompatible($pos.pos, before.type, before.contentMatchAt(before.childCount))
        .join($pos.pos)
        .scrollIntoView(),
    )
  }
  return true
}

// Never exported from 'prosemirror-commands' but needed for the plucked out commands
// TODO: Take away all ! forced assumptions
export function deleteBarrier<S extends Schema = any>(
  state: EditorState<S>,
  $cut: ResolvedPos<S>,
  dispatch?: EditorView<S>['dispatch'],
) {
  let before = $cut.nodeBefore,
    after = $cut.nodeAfter,
    conn: NodeType<S>[],
    match: ContentMatch<S>
  if (!before || !after) {
    return false
  }
  if (before?.type.spec.isolating || after?.type.spec.isolating) {
    return false
  }
  if (joinMaybeClear(state, $cut, dispatch)) {
    return true
  }

  let canDelAfter = $cut.parent.canReplace($cut.index(), $cut.index() + 1)
  if (
    canDelAfter && // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (conn = (match = before.contentMatchAt(before.childCount)).findWrapping(after.type)!) &&
    match.matchType(conn[0] || after.type)?.validEnd
  ) {
    if (dispatch) {
      let end = $cut.pos + after.nodeSize,
        wrap = Fragment.empty
      for (let i = conn.length - 1; i >= 0; i--) {
        wrap = Fragment.from(conn[i].create(null, wrap))
      }
      wrap = Fragment.from(before.copy(wrap))
      let tr = state.tr.step(
        new ReplaceAroundStep($cut.pos - 1, end, $cut.pos, end, new Slice(wrap, 1, 0), conn.length, true),
      )
      let joinAt = end + 2 * conn.length
      if (canJoin(tr.doc, joinAt)) {
        tr.join(joinAt)
      }
      dispatch(tr.scrollIntoView())
    }
    return true
  }

  let selAfter = Selection.findFrom($cut, 1)
  let range = selAfter && selAfter.$from.blockRange(selAfter.$to),
    target = range && liftTarget(range)
  if (target != null && target >= $cut.depth) {
    if (dispatch && range) dispatch(state.tr.lift(range, target).scrollIntoView())
    return true
  }

  if (canDelAfter && textblockAt(after, 'start', true) && textblockAt(before, 'end')) {
    let at = before,
      wrap = []
    for (;;) {
      wrap.push(at)
      if (at.isTextblock) break
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      at = at.lastChild!
    }
    let afterText = after,
      afterDepth = 1
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    for (; !afterText.isTextblock; afterText = afterText.firstChild!) afterDepth++
    if (at.canReplace(at.childCount, at.childCount, afterText.content)) {
      if (dispatch) {
        let end = Fragment.empty
        for (let i = wrap.length - 1; i >= 0; i--) end = Fragment.from(wrap[i].copy(end))
        let tr = state.tr.step(
          new ReplaceAroundStep(
            $cut.pos - wrap.length,
            $cut.pos + after.nodeSize,
            $cut.pos + afterDepth,
            $cut.pos + after.nodeSize - afterDepth,
            new Slice(end, wrap.length, 0),
            0,
            true,
          ),
        )
        dispatch(tr.scrollIntoView())
      }
      return true
    }
  }

  return false
}
